import type { Translate } from "@/lib/i18n/translate";
import {
  ArrowLeft,
  EllipsisVertical,
  Inbox,
  Mic,
  Phone,
  PhoneIncoming,
  Play,
  Voicemail,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import { HeroVorgangActionFooter } from "./hero-vorgang-action-footer";
import { HeroVorgangDetailRail } from "./hero-vorgang-detail-rail";
import type { HeroVorgangRailData } from "./hero-vorgang-detail-rail";

function anrufRail(t: Translate): HeroVorgangRailData {
  return {
    externalId: "#2026-23901",
    title: t("anrufMarkusVogt8211"),
    statusLabel: "TO-DO",
    assignee: { initials: "NH", name: t("ninaHartmann"), bg: "#0B0F19" },
    board: { name: t("telefonanlage2"), icon: "inbox" },
    recipient: { name: t("markusVogt"), source: t("anruf") },
    property: { name: t("ahornweg5"), unit: "WE 7 · EG" },
    openPickers: ["due", "tags", "description"],
  };
}

/**
 * KI-Anrufbeantworter module — full-bleed window interior (no 220px sidebar).
 * A voicemail-derived Vorgang on the left + a detail rail on the right, with a
 * floating "Aus der Telefonanlage" callout overlaying the bottom of the window.
 * Ported 1:1 from the prototype (sc-if isAnrufbeantworter, content + overlay).
 */
export function ModuleAnrufbeantworter(_props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleAnrufbeantworter");
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        height: MODULE_HEIGHT,
        background: "#fff",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* VORGANG */}
          <div
            style={{
              position: "relative",
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "13px 18px",
                borderBottom: "1px solid #F1F2F4",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  color: "rgba(11,15,25,0.45)",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <ArrowLeft width={18} height={18} strokeWidth={1.8} />
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#0B0F19",
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    color: H.accent,
                    display: "inline-flex",
                    flexShrink: 0,
                  }}
                >
                  <PhoneIncoming width={16} height={16} strokeWidth={1.9} />
                </span>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {t("anrufMarkusVogt8211")}
                </span>
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  display: "inline-flex",
                  color: "rgba(11,15,25,0.4)",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <EllipsisVertical width={18} height={18} strokeWidth={1.8} />
              </span>
            </div>
            <div
              style={{
                position: "relative",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  padding: "16px 18px",
                  paddingBottom: 56,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: H.accentSoft,
                    border: `1px solid ${H.accentBorder}`,
                    borderRadius: 12,
                    padding: "11px 14px",
                  }}
                >
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      background: H.accent,
                      color: "#fff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Phone width={16} height={16} strokeWidth={1.9} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: H.label,
                        fontSize: 10,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: H.accent,
                        fontWeight: 700,
                      }}
                    >
                      {t("anruf")}
                    </div>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "#0B0F19",
                        marginTop: 1,
                      }}
                    >
                      {t("k491512240187183")}
                    </div>
                  </div>
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: "#EAEFE9",
                      color: "#5E7C66",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {t("mv")}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 16,
                    border: "1px solid #ECEDF0",
                    borderRadius: 12,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      padding: "11px 14px",
                      borderBottom: "1px solid #F3F4F6",
                    }}
                  >
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: "#EAEFE9",
                        color: "#5E7C66",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      {t("mv")}
                    </span>
                    <span
                      style={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: "#0B0F19",
                      }}
                    >
                      {t("markusVogt")}
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "2px 8px",
                        border: `1px solid ${H.accentBorder}`,
                        borderRadius: 6,
                        fontSize: 11,
                        color: H.accent,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      <PhoneIncoming width={11} height={11} strokeWidth={1.9} />
                      {t("eingehenderAnruf")}
                    </span>
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 11.5,
                        color: "rgba(11,15,25,0.4)",
                      }}
                    >
                      {t("k1406202609")}
                    </span>
                  </div>
                  <div
                    style={{
                      padding: "13px 15px",
                      fontSize: 13,
                      lineHeight: 1.7,
                      color: "#2A2F3A",
                    }}
                  >
                    <div>
                      {t.rich("anrufVonNummer", {
                        dim: (chunks: ReactNode): ReactNode => (
                          <span style={{ color: "rgba(11,15,25,0.5)" }}>
                            {chunks}
                          </span>
                        ),
                      })}
                    </div>
                    <div>
                      {t.rich("angerufeneNummerZentrale", {
                        dim: (chunks: ReactNode): ReactNode => (
                          <span style={{ color: "rgba(11,15,25,0.5)" }}>
                            {chunks}
                          </span>
                        ),
                      })}
                    </div>
                    <div
                      style={{
                        marginTop: 9,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontWeight: 600,
                        color: "#0B0F19",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          background: H.accentSoft,
                          color: H.accent,
                        }}
                      >
                        <Play width={12} height={12} strokeWidth={2} />
                      </span>
                      {t("sprachnachricht018")}
                    </div>
                    <div
                      style={{
                        marginTop: 9,
                        paddingLeft: 2,
                        color: "#3D3A34",
                      }}
                    >
                      {t("k8222GutenTagHier")}
                    </div>
                  </div>
                </div>
              </div>
              <HeroVorgangActionFooter />
            </div>
          </div>

          <HeroVorgangDetailRail rail={anrufRail(t)} />
        </div>
      </div>
    </div>
  );
}

/**
 * The "Aus der Telefonanlage" floating bar (frame-level overlay), prototype
 * line 1869. Positions itself absolutely relative to the aurora frame so it
 * overflows the window (rendered via the registry `Overlay` slot).
 */
export function AnrufbeantworterOverlay(): ReactNode {
  const t = useTranslations("marketing.moduleAnrufbeantworter");
  return (
    <div
      style={{
        position: "absolute",
        left: 16,
        right: 16,
        bottom: 16,
        background: "#fff",
        border: "1.5px solid #E2E5EA",
        borderRadius: 14,
        boxShadow:
          "0 38px 76px -22px rgba(11,15,25,0.50), 0 14px 30px -12px rgba(11,15,25,0.30)",
        zIndex: 30,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -12,
          left: 18,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "#0B0F19",
          color: "#fff",
          fontFamily: H.label,
          fontSize: 9.5,
          letterSpacing: "0.11em",
          textTransform: "uppercase",
          padding: "4px 10px",
          borderRadius: 7,
          boxShadow: "0 6px 14px -4px rgba(11,15,25,0.5)",
        }}
      >
        <PhoneIncoming width={11} height={11} strokeWidth={2} />
        {t("telefonanlage")}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "15px 18px",
        }}
      >
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            background: "#EDEFF2",
            color: "#5B6472",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12.5,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {t("mv")}
        </span>
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0B0F19",
              whiteSpace: "nowrap",
            }}
          >
            {t.rich("anruferNameUndNummer", {
              dim: (chunks: ReactNode): ReactNode => (
                <span style={{ color: "rgba(11,15,25,0.45)", fontWeight: 500 }}>
                  {chunks}
                </span>
              ),
            })}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 3,
              whiteSpace: "nowrap",
              fontSize: 12.5,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                color: H.accent,
                fontWeight: 600,
                textDecoration: "underline",
                textUnderlineOffset: 2,
                cursor: "pointer",
              }}
            >
              <Voicemail width={14} height={14} strokeWidth={2} />
              {t("nachrichtAbspielen")}
            </span>
            <span style={{ color: "rgba(11,15,25,0.3)" }}>{t("k183")}</span>
            <span style={{ color: "#C0453E", fontWeight: 500 }}>
              {t("nichtErreicht")}
            </span>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            height: 32,
            padding: "0 12px",
            border: "1px solid #E7E9ED",
            borderRadius: 9,
            fontSize: 12.5,
            color: "rgba(11,15,25,0.6)",
            flexShrink: 0,
          }}
        >
          <Inbox width={14} height={14} strokeWidth={1.8} />
          {t("anruf491512240187")}
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 30,
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "rgba(11,15,25,0.5)",
            fontSize: 13,
            fontStyle: "italic",
          }}
        >
          <Mic
            width={14}
            height={14}
            strokeWidth={1.8}
            style={{ flexShrink: 0 }}
          />
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("k8222GutenTagHier2")}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 12.5,
              color: "rgba(11,15,25,0.4)",
              whiteSpace: "nowrap",
            }}
          >
            {t("k14060912")}
          </span>
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "#0B0F19",
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {t("nh")}
          </span>
        </div>
      </div>
    </div>
  );
}
